import { useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import {
  IStatementActivityLookback,
  IStatementBuilderMessage,
  IStatementTemplateOptions,
  OrganisationFeatureFlags,
} from 'shared';
import { toast } from 'react-toastify';
import { useIntercom } from 'react-use-intercom';
import { BeakerIcon } from '@heroicons/react/20/solid';

import { Button } from '../../../../../../common/Atoms/Button';
import { InputTextAddon } from '../../../../../../common/Atoms/InputTextAddon';
import { Modal } from '../../../../../../common/Atoms/Modal';
import { Notification } from '../../../../../../common/Atoms/Notification';
import { Heading } from '../../../../../../common/Atoms/Typography/Heading';
import { Paragraph } from '../../../../../../common/Atoms/Typography/Paragraph';
import { Spinner } from '../../../../Common/Spinner';
import { useGetSelectedOrganisation } from '../../../../../hooks/useGetSelectedOrganisation';
import { useUser } from '../../../../../hooks/useUser';
import { useGiveAssistantConsentMutation } from '../../../../../services/api/assistantApi/assistant';
import { IPickedContact } from '../../../../Common/ContactSearch';
import {
  useCreateStatementBuilderTemplateMutation,
  useDeleteStatementBuilderTemplateMutation,
  useGetStatementBuilderPreviewQuery,
  useGetStatementBuilderStateQuery,
  useGetStatementBuilderTemplatesQuery,
  useRenameStatementBuilderTemplateMutation,
  usePublishStatementBuilderMutation,
  useResetStatementBuilderMutation,
  useRevertStatementBuilderToMessageMutation,
  useSendStatementBuilderMessageMutation,
  useUpdateStatementBuilderActivityLookbackMutation,
  useUpdateStatementBuilderEnabledMutation,
  useUpdateStatementBuilderOptionsMutation,
} from '../../../../../services/api/statementBuilderApi/statementBuilder';

import { BuilderActivityPeriod } from './BuilderActivityPeriod';
import { BuilderHeader, TemplateAction } from './BuilderHeader';
import { BuilderChat } from './BuilderChat';
import { BuilderConfirmModal } from './BuilderConfirmModal';
import { BuilderPreview } from './BuilderPreview';
import { BuilderStatementOptions } from './BuilderStatementOptions';

type ApiMutationError = {
  error?: {
    data?: {
      message?: string;
      error?: string;
    } | string;
  };
};

function getErrorMessage(error: unknown, fallbackMessage: string) {
  const maybeError = error as ApiMutationError;
  const errorData = maybeError?.error?.data;

  if (typeof errorData === `string` && errorData.trim().length > 0) {
    return errorData;
  }

  if (typeof errorData === `object` && errorData?.message) {
    return errorData.message;
  }

  if (typeof errorData === `object` && errorData?.error) {
    return errorData.error;
  }

  return fallbackMessage;
}

type PendingConfirm =
  | { kind: `revert`; message: IStatementBuilderMessage }
  | { kind: `publish` }
  | { kind: `reset` }
  | { kind: `delete` };

type TitleAction = Exclude<TemplateAction, `delete`>;

const TITLE_MODAL_COPY: Record<TitleAction, { heading: string; confirmLabel: string }> = {
  create: { heading: `New template`, confirmLabel: `Create` },
  duplicate: { heading: `Duplicate template`, confirmLabel: `Duplicate` },
  rename: { heading: `Rename template`, confirmLabel: `Save` },
};

const CONFIRM_COPY: Record<PendingConfirm[`kind`], { title: string; message: string; confirmLabel: string; destructive?: boolean }> = {
  revert: {
    title: `Revert draft`,
    message: `Revert the draft to this version? Later changes stay in the chat history and can be restored.`,
    confirmLabel: `Revert`,
  },
  publish: {
    title: `Publish draft`,
    message: `Publish this draft and make it live for your organisation?`,
    confirmLabel: `Publish`,
  },
  reset: {
    title: `Reset draft`,
    message: `Reset this draft to the base template and start a new chat? This cannot be undone.`,
    confirmLabel: `Reset to Base`,
    destructive: true,
  },
  delete: {
    title: `Delete template`,
    message: `Delete this template, its chat and its version history? This cannot be undone.`,
    confirmLabel: `Delete`,
    destructive: true,
  },
};

export function StatementBuilderRoot() {
  const currentOrg = useGetSelectedOrganisation();
  const canUseCustomStatement = !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_STATEMENT_BUILDER);
  const statementBuilderEnabled = canUseCustomStatement || !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_STATEMENT_BUILDER_PARTIAL);
  const organisationId = currentOrg?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const [previewContact, setPreviewContact] = useState<IPickedContact | undefined>();
  const [titleAction, setTitleAction] = useState<TitleAction | null>(null);
  const [titleInput, setTitleInput] = useState(``);

  const { data: templatesData } = useGetStatementBuilderTemplatesQuery(
    { organisationId: organisationId || `` },
    { skip: !organisationId },
  );
  const templates = templatesData?.templates || [];
  const requestedTemplateId = Number(searchParams.get(`template`));
  const templateId = templates.find(template => template.id === requestedTemplateId)?.id ?? templates[0]?.id ?? 0;
  const currentTemplate = templates.find(template => template.id === templateId);
  const skip = !organisationId || !templateId;
  const queryArgs = { organisationId: organisationId || ``, templateId };

  const [polling, setPolling] = useState(false);
  const {
    // currentData, not data: data keeps the previous template while the next one loads,
    // which would show its state while the actions already target the new template
    currentData: stateData,
  } = useGetStatementBuilderStateQuery(
    queryArgs,
    // AI edits run on a worker; poll until the running placeholder is filled in
    { skip, pollingInterval: polling ? 1500 : 0 },
  );
  const {
    currentData: previewData,
    isFetching: previewFetching,
    refetch: refetchPreview,
  } = useGetStatementBuilderPreviewQuery({ ...queryArgs, contactId: previewContact?.id }, { skip });
  const editRunning = !!stateData?.messages.some(message => message.status === `running`);

  useEffect(() => {
    if (polling && !editRunning) {
      // The edit finished, so the draft may have changed
      refetchPreview();
    }

    setPolling(editRunning);
  }, [editRunning, polling, refetchPreview]);

  const [sendMessage, sendMessageState] = useSendStatementBuilderMessageMutation();
  const [revertToMessage, revertState] = useRevertStatementBuilderToMessageMutation();
  const [publishDraft, publishState] = usePublishStatementBuilderMutation();
  const [updateEnabled, toggleState] = useUpdateStatementBuilderEnabledMutation();
  const [resetDraft, resetState] = useResetStatementBuilderMutation();
  const [updateActivityLookback, activityLookbackState] = useUpdateStatementBuilderActivityLookbackMutation();
  const [updateOptions, optionsState] = useUpdateStatementBuilderOptionsMutation();
  const [createTemplate, createState] = useCreateStatementBuilderTemplateMutation();
  const [renameTemplate, renameState] = useRenameStatementBuilderTemplateMutation();
  const [deleteTemplate, deleteState] = useDeleteStatementBuilderTemplateMutation();
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const { data: user } = useUser();
  const [giveConsent, consentState] = useGiveAssistantConsentMutation();
  // The message held back while the org is asked to consent to AI data use
  const [consentPrompt, setConsentPrompt] = useState<string | null>(null);

  // The held message was written for one org and template, so drop it if either changes
  // (browser back/forward still works behind the consent modal)
  useEffect(() => {
    setConsentPrompt(null);
  }, [organisationId, templateId]);

  const { show: showIntercom } = useIntercom();

  if (!statementBuilderEnabled) {
    return <Navigate to={ `..` }
      replace />;
  }

  if (!stateData || !currentTemplate) {
    return (
      <div className={ `flex min-h-[60vh] items-center justify-center` }>
        <Spinner
          center
          size={ `120px` }
          message={ `Loading AI Statement Builder...` }
        />
      </div>
    );
  }

  const isSending = sendMessageState.isLoading || editRunning;
  const busy = isSending
    || revertState.isLoading
    || publishState.isLoading
    || toggleState.isLoading
    || resetState.isLoading
    || activityLookbackState.isLoading
    || optionsState.isLoading
    || createState.isLoading
    || renameState.isLoading
    || deleteState.isLoading;

  function selectTemplate(id: number) {
    setSearchParams({ template: String(id) });
  }

  function handleTemplateAction(action: TemplateAction) {
    if (action === `delete`) {
      setPendingConfirm({ kind: `delete` });

      return;
    }

    setTitleInput(action === `create` ? `` : action === `duplicate` ? `${currentTemplate.title} (copy)` : currentTemplate.title);
    setTitleAction(action);
  }

  async function submitTitle() {
    if (!organisationId || !titleAction || !titleInput.trim()) {
      return;
    }

    try {
      if (titleAction === `rename`) {
        await renameTemplate({ organisationId, templateId, title: titleInput.trim() }).unwrap();
        toast.success(`Template renamed.`);
      }
      else {
        const { template } = await createTemplate({
          organisationId,
          title: titleInput.trim(),
          duplicateFromId: titleAction === `duplicate` ? templateId : undefined,
        }).unwrap();

        selectTemplate(template.id);
        toast.success(titleAction === `duplicate` ? `Template duplicated.` : `Template created.`);
      }

      setTitleAction(null);
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not save the template.`));
    }
  }

  async function deleteNow() {
    if (!organisationId) {
      return;
    }

    try {
      await deleteTemplate({ organisationId, templateId }).unwrap();

      const remaining = templates.find(template => template.id !== templateId);

      if (remaining) {
        selectTemplate(remaining.id);
      }

      toast.success(`Template deleted.`);
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not delete this template.`));
    }
  }

  async function sendNow(prompt: string) {
    if (!organisationId) {
      return;
    }

    try {
      await sendMessage({ organisationId, templateId, prompt }).unwrap();
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not send your message.`));
    }
  }

  async function handleSendMessage(prompt: string) {
    // Messages go to our AI provider, so the org has to agree first. Admin override sessions bypass this, like the assistant
    if (currentOrg?.aiConsentGiven !== true && !user?.adminOverride) {
      setConsentPrompt(prompt);

      return;
    }

    await sendNow(prompt);
  }

  async function consentAndSend() {
    if (!organisationId || consentPrompt === null) {
      return;
    }

    try {
      await giveConsent({ organisationId }).unwrap();
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not record your consent.`));

      return;
    }

    setConsentPrompt(null);
    await sendNow(consentPrompt);
  }

  async function revertNow(message: IStatementBuilderMessage) {
    if (!organisationId) {
      return;
    }

    try {
      await revertToMessage({
        organisationId,
        templateId,
        messageId: message.id,
      }).unwrap();

      toast.success(`Draft reverted.`);
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not revert this draft.`));
    }
  }

  async function publishNow() {
    if (!organisationId) {
      return;
    }

    try {
      await publishDraft({ organisationId, templateId }).unwrap();
      toast.success(`Draft published.`);
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not publish this draft.`));
    }
  }

  async function handleToggleEnabled(enabled: boolean) {
    if (!organisationId) {
      return;
    }

    try {
      await updateEnabled({
        organisationId,
        templateId,
        enabled,
      }).unwrap();

      toast.success(enabled ? `${currentTemplate.title} is now the default statement.` : `Statements now use the standard template.`);
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not update statement mode.`));
    }
  }

  async function resetNow() {
    if (!organisationId) {
      return;
    }

    try {
      await resetDraft({ organisationId, templateId }).unwrap();
      toast.success(`Draft reset to base template.`);
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not reset the draft.`));
    }
  }

  async function saveActivityLookback(activityLookback: IStatementActivityLookback) {
    if (!organisationId) {
      return;
    }

    try {
      await updateActivityLookback({ organisationId, templateId, ...activityLookback }).unwrap();
      toast.success(`Recent activity period saved to the draft.`);
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not save the recent activity period.`));
    }
  }

  async function saveOptions(options: IStatementTemplateOptions) {
    if (!organisationId) {
      return;
    }

    try {
      await updateOptions({ organisationId, templateId, ...options }).unwrap();
      toast.success(`Aged analysis and grouping saved to the draft.`);
    }
    catch (error) {
      toast.error(getErrorMessage(error, `Could not save the aged analysis and grouping.`));
    }
  }

  async function handleConfirm() {
    if (!pendingConfirm) {
      return;
    }

    if (pendingConfirm.kind === `revert`) {
      await revertNow(pendingConfirm.message);
    }
    else if (pendingConfirm.kind === `publish`) {
      await publishNow();
    }
    else if (pendingConfirm.kind === `delete`) {
      await deleteNow();
    }
    else {
      await resetNow();
    }

    setPendingConfirm(null);
  }

  const confirmCopy = pendingConfirm ? CONFIRM_COPY[pendingConfirm.kind] : null;

  return (
    <div className={ `flex flex-col gap-4` }>
      <Notification
        type={ `pro` }
        icon={ BeakerIcon }
      >
        { `The AI Statement Builder is new. If you run into a problem or want something added, ` }
        <button
          type={ `button` }
          className={ `cursor-pointer font-medium underline` }
          onClick={ () => showIntercom() }
        >
          { `chat with support` }
        </button>
        { `.` }
      </Notification>

      <BuilderHeader
        templates={ templates }
        templateId={ templateId }
        onSelectTemplate={ selectTemplate }
        onTemplateAction={ handleTemplateAction }
        hasPublished={ stateData.hasPublished }
        versions={ stateData.versions }
        draftIsPublished={ stateData.draftIsPublished }
        enabled={ stateData.enabled }
        canEnable={ canUseCustomStatement }
        busy={ busy }
        onToggleEnabled={ handleToggleEnabled }
        onPublish={ async() => setPendingConfirm({ kind: `publish` }) }
        onReset={ async() => setPendingConfirm({ kind: `reset` }) }
      />

      <BuilderConfirmModal
        open={ pendingConfirm !== null }
        title={ confirmCopy?.title || `` }
        message={ confirmCopy?.message || `` }
        confirmLabel={ confirmCopy?.confirmLabel || `Confirm` }
        destructive={ confirmCopy?.destructive }
        busy={ busy }
        onConfirm={ handleConfirm }
        onCancel={ () => setPendingConfirm(null) }
      />

      <BuilderConfirmModal
        open={ consentPrompt !== null }
        title={ `Consent needed to use the AI builder` }
        message={ `We take AI and your data seriously, so we need your permission before the builder can use it. Your message will be sent once you consent.` }
        confirmLabel={ `I consent on behalf of my organisation` }
        busy={ consentState.isLoading }
        onConfirm={ consentAndSend }
        onCancel={ () => setConsentPrompt(null) }
      />

      <Modal
        title={ titleAction ? TITLE_MODAL_COPY[titleAction].heading : `` }
        open={ titleAction !== null }
        setOpen={ () => setTitleAction(null) }
      >
        <form
          className={ `mt-4 space-y-4` }
          onSubmit={ event => {
            event.preventDefault();
            submitTitle();
          } }
        >
          <InputTextAddon
            label={ `Title` }
            name={ `templateTitle` }
            value={ titleInput }
            maxLength={ 100 }
            onChange={ ({ value }) => setTitleInput(String(value)) }
          />
          <div className={ `flex justify-end gap-3` }>
            <Button
              color={ `gray` }
              disabled={ busy }
              onClick={ () => setTitleAction(null) }
            >
              { `Cancel` }
            </Button>
            <Button
              type={ `submit` }
              disabled={ busy || !titleInput.trim() }
            >
              { titleAction ? TITLE_MODAL_COPY[titleAction].confirmLabel : `` }
            </Button>
          </div>
        </form>
      </Modal>

      { /* Chat and preview keep a full screen height; the settings below scroll into view */ }
      <div className={ `grid h-[calc(100vh-12.5rem)] min-h-[680px] grid-cols-[420px_minmax(0,1fr)] gap-4` }>
        <BuilderChat
          messages={ stateData.messages }
          isSending={ isSending }
          onSendMessage={ handleSendMessage }
          onRevertMessage={ async message => setPendingConfirm({ kind: `revert`, message }) }
        />

        <BuilderPreview
          html={ previewData?.html || `` }
          loading={ previewFetching || busy }
          organisationId={ organisationId || `` }
          templateId={ templateId }
          contact={ previewContact }
          onContactChange={ setPreviewContact }
        />
      </div>

      <div className={ `rounded-xl border border-slate-200 bg-white px-5 py-4` }>
        <BuilderActivityPeriod
          activityLookback={ stateData.activityLookback }
          busy={ busy }
          onSave={ saveActivityLookback }
        />
        <BuilderStatementOptions
          options={ stateData.options }
          busy={ busy }
          onSave={ saveOptions }
        />
      </div>

      <div className={ `px-1` }>
        <Heading
          size={ `sm` }
          variant={ `secondary` }
        >
          { `Draft-only mode` }
        </Heading>
        <Paragraph
          size={ `xs` }
          variant={ `secondary` }
          className={ `mt-1` }
        >
          { `AI edits change your draft only. Use Publish when you are ready for customers to see the updated statement.` }
        </Paragraph>
      </div>
    </div>
  );
}
